import React from 'react';
import { CLOCK_IN_OUT, CLOCK_IN_OUT_PAGES } from './constants';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import withShipment from '../../withShipment';
import { statusSelector } from '../../redux/selectors/global';
import { clearStatus } from '../../redux/actions/global';
import './ClockInOut.css';
import { userInfoSelector } from '../../redux/selectors/auth';
import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';
import { Button } from '@material-ui/core';
import { clockRecordSelector } from '../../redux/selectors/settings';
import {
  clockIn,
  clockOut,
  loadClockRecords,
} from '../../redux/actions/settings';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PendingPhotoGate from '../bonus/PendingPhotoGate';
import { pendingPhotoStatus } from '../bonus/pendingPhotos';
import ConfirmDialog from '../global/ConfirmDialog';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';

class ClockInOut extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: CLOCK_IN_OUT_PAGES.TABLE,
      feedbackBarOpen: false,
      error: false,
      feedbackStatus: '',
      feedbackMessage: '',
      timeIn: null,
      timeOut: null,
      isDialogOpen: false,
      isClockInDialogOpen: false,
      isPhotoGateOpen: false,
      clockOutProcessing: false,
      currentDuration: '00:00:00',
      startTimeLabel: '',
      intervalId: null,
      clockInProcessing: false,
    };
  }

  componentDidMount() {
    this.props.loadClockRecords({ userId: this.props.user.userId });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar(this.props.status.statusText, 'error');
    }

    if (prevProps.clockRecord !== this.props.clockRecord) {
      const clockRecord = this.props.clockRecord;

      // Check if clockRecord is defined
      if (clockRecord) {
        const { timeIn, timeOut } = clockRecord;

        // Convert backend `timeIn` object to JavaScript `Date`
        if (timeIn) {
          const parsedTimeIn = new Date(
            timeIn.year,
            timeIn.monthValue - 1, // Month is 0-indexed in JS Date
            timeIn.dayOfMonth,
            timeIn.hour,
            timeIn.minute,
            timeIn.second,
            Math.floor(timeIn.nano / 1e6) // Convert nanoseconds to milliseconds
          );
          this.setState({ timeIn: parsedTimeIn }, this.startTimer);
        }

        if (timeOut) {
          const parsedTimeOut = new Date(
            timeOut.year,
            timeOut.monthValue - 1, // Month is 0-indexed in JS Date
            timeOut.dayOfMonth,
            timeOut.hour,
            timeOut.minute,
            timeOut.second,
            Math.floor(timeOut.nano / 1e6) // Convert nanoseconds to milliseconds
          );

          this.setState({ timeOut: parsedTimeOut }, this.stopTimer);
        }

        if (
          this.state.timeIn &&
          !this.state.timeOut &&
          prevState.timeIn !== this.state.timeIn
        ) {
          this.startTimer();
        }
      } else {
        console.warn('clockRecord is undefined');
      }
    }
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  // A bonus photo still in flight is lost the moment the shift ends, so the
  // photo gate comes first and the ordinary confirm only follows once nothing
  // is outstanding.
  //
  // The count has to come from the SERVER. Clock-out runs on the shop
  // computer, the photo is sitting in an outbox on the employee's phone, and
  // this browser cannot see that. Asking the local outbox here would read zero
  // every time and the gate would never fire — see pendingPhotos.js.
  handleClockOutClick = () => {
    pendingPhotoStatus(this.props.user)
      .catch(() => ({ count: 0 }))
      .then(({ count }) => {
        if (count > 0) {
          this.setState({ isPhotoGateOpen: true });
          return;
        }
        this.setState({ isDialogOpen: true });
      });
  };

  closePhotoGate = () => {
    this.setState({ isPhotoGateOpen: false });
  };

  // The gate is satisfied — every photo landed, or the employee accepted the
  // loss. Hand over to the normal clock-out confirmation.
  proceedPastPhotoGate = () => {
    this.setState({ isPhotoGateOpen: false, isDialogOpen: true });
  };

  cancelClockOut = () => {
    this.setState({ isDialogOpen: false });
  };

  getGreeting = () => {
    // Create a date object for the current time in the America/Chicago timezone
    const currentTime = new Date().toLocaleString('en-US', {
      timeZone: 'America/Chicago',
    });

    // Extract the hour (0-23) from the current time in the Chicago timezone
    const currentHour = new Date(currentTime).getHours();

    if (currentHour >= 0 && currentHour < 12) {
      return 'Good Morning';
    } else if (currentHour >= 12 && currentHour < 17) {
      return 'Good Afternoon';
    } else if (currentHour >= 17 && currentHour < 20) {
      return 'Good Evening';
    } else {
      return 'Good Night';
    }
  };

  startTimer = () => {
    if (!this.state.intervalId) {
      // Start only if no timer is running
      const intervalId = setInterval(this.updateDuration, 1000);
      this.setState({ intervalId });
    }
  };

  stopTimer = () => {
    clearInterval(this.state.intervalId);
    this.setState({ intervalId: null });
  };

  // Clocking in used to fire straight off the button with no confirmation,
  // so a mis-tap silently opened a shift — and every bonus, transaction and
  // ticket the employee then entered is bound to that session.
  handleClockInClick = () => {
    // Stamped at open, not in render: the dialog should show the time the
    // employee is agreeing to, not a clock that ticks while they read it.
    this.setState({
      isClockInDialogOpen: true,
      startTimeLabel: new Date().toLocaleTimeString('en-US', {
        timeZone: 'America/Chicago',
        hour: 'numeric',
        minute: '2-digit',
      }),
    });
  };

  cancelClockIn = () => {
    this.setState({ isClockInDialogOpen: false });
  };

  confirmClockIn = () => {
    this.setState({ isClockInDialogOpen: false }, this.clockIn);
  };

  clockIn = () => {
    this.setState({ clockInProcessing: true });

    const callback = (error, response) => {
      if (error) {
        this.openFeedbackBar('error', response);
      } else {
        this.openFeedbackBar('success', 'Clock Started');
        this.startTimer();
        window.location.reload();
      }
      this.setState({ clockInProcessing: false });
    };
    this.props.clockIn({ userId: this.props.user.userId }, callback);
  };

  confirmClockOut = () => {
    this.setState({ clockOutProcessing: true });
    const callback = (error, response) => {
      if (error) {
        this.openFeedbackBar('error', response);
      } else {
        this.openFeedbackBar('success', 'Clock Stopped');
        this.stopTimer();
        window.location.reload();
      }
      this.setState({ clockOutProcessing: false });
      this.cancelClockOut();
    };
    this.props.clockOut(
      {
        userId: this.props.user.userId,
      },
      callback
    );
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  convertToChicagoTime = (date) => {
    return new Date(
      date.toLocaleString('en-US', { timeZone: 'America/Chicago' })
    );
  };

  updateDuration = () => {
    const { timeIn, timeOut } = this.state;

    if (timeIn) {
      // Use timeOut if available, otherwise use the current time in America/Chicago zone
      const endTime = timeOut || this.convertToChicagoTime(new Date());
      const diffInMs = endTime - timeIn;

      const hours = Math.floor(diffInMs / (1000 * 60 * 60))
        .toString()
        .padStart(2, '0');
      const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60))
        .toString()
        .padStart(2, '0');
      const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000)
        .toString()
        .padStart(2, '0');

      this.setState({ currentDuration: `${hours}:${minutes}:${seconds}` });

      // If timeOut exists, clear any interval updating currentDuration
      if (timeOut && this.durationInterval) {
        clearInterval(this.durationInterval);
        this.durationInterval = null;
      }
    }
  };

  // updateDuration = () => {
  //     const { timeIn, timeOut } = this.state;
  //
  //     if (timeIn) {
  //         // If timeOut is set, calculate the total duration and stop further updates
  //         const endTime = timeOut || new Date(); // Use `timeOut` if it exists, otherwise use current time
  //         const diffInMs = endTime - timeIn;
  //
  //         const hours = Math.floor(diffInMs / (1000 * 60 * 60)).toString().padStart(2, '0');
  //         const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0');
  //         const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000).toString().padStart(2, '0');
  //
  //         this.setState({ currentDuration: `${hours}:${minutes}:${seconds}` });
  //
  //         // If timeOut exists, clear any interval updating currentDuration
  //         if (timeOut && this.durationInterval) {
  //             clearInterval(this.durationInterval);
  //             this.durationInterval = null;
  //         }
  //     }
  // };

  renderSessionInfo = () => {
    const { timeIn, timeOut, currentDuration } = this.state;

    return (
      <div className="session-info">
        <AccessTimeIcon
          style={{ fontSize: 60, color: '#2596be', marginBottom: '1rem' }}
        />

        {timeIn && (
          <p>
            <b>Date:</b>{' '}
            {`${timeIn.toLocaleDateString('en-GB', {
              weekday: 'long',
            })} ${String(timeIn.getDate()).padStart(2, '0')}/${String(
              timeIn.getMonth() + 1
            ).padStart(2, '0')}/${timeIn.getFullYear()}`}
          </p>
        )}

        <p>
          <b>Clock In:</b>{' '}
          {timeIn ? timeIn.toLocaleTimeString() : 'Not clocked in yet'}
        </p>
        <p>
          <b>Clock Out:</b>{' '}
          {timeOut ? timeOut.toLocaleTimeString() : 'Not clocked out yet'}
        </p>

        {this.state.timeIn && !this.state.timeOut && (
          <p className="duration">
            Current Session: <strong>{currentDuration}</strong>
          </p>
        )}

        {this.state.timeOut && (
          <p className="duration">
            Total Time Worked: <strong>{currentDuration}</strong>
          </p>
        )}
      </div>
    );
  };

  displayTable = () => {
    return (
      <div>
        <div className="clock-in-out-container" style={{ padding: '1rem' }}>
          <p className="duration">
            {this.getGreeting()}, <strong> {this.props.user.firstname}!</strong>
          </p>

          <div className="actions">
            <Button
              variant="outlined"
              className="check-in-btn"
              onClick={this.handleClockInClick}
              // disabled={!!this.state.timeIn && !this.state.timeOut}
              disabled={
                this.state.clockInProcessing ||
                (!!this.state.timeIn && !this.state.timeOut)
              }
            >
              Clock In
            </Button>
            <Button
              className="check-out-btn"
              variant="contained"
              onClick={this.handleClockOutClick}
              disabled={!this.state.timeIn || !!this.state.timeOut}
            >
              Clock Out
            </Button>
          </div>

          {this.renderSessionInfo()}
        </div>
      </div>
    );
  };

  render() {
    return (
      <React.Fragment>
        {/*<ErrorFeedbackBar/>*/}
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />
        <TopToolbar pageName={CLOCK_IN_OUT} />

        <PendingPhotoGate
          open={this.state.isPhotoGateOpen}
          actionLabel="Clock Out"
          onCancel={this.closePhotoGate}
          onProceed={this.proceedPastPhotoGate}
        />

        <ConfirmDialog
          open={this.state.isClockInDialogOpen}
          tone="success"
          icon={<PlayCircleOutlineIcon />}
          title="Start your shift?"
          message="You'll be on the clock from now until you clock out. Everything you enter — bonuses, tickets, transactions — is recorded against this session."
          fact={this.state.startTimeLabel}
          factLabel="Starting"
          confirmLabel="Clock In"
          cancelLabel="Not yet"
          busy={this.state.clockInProcessing}
          onConfirm={this.confirmClockIn}
          onCancel={this.cancelClockIn}
        />

        <ConfirmDialog
          open={this.state.isDialogOpen}
          tone="warning"
          icon={<AccessTimeIcon />}
          title="End your shift?"
          message="You'll be clocked out and this session closes. Anything you still need to enter has to go in before you do."
          fact={this.state.currentDuration}
          factLabel="Time on shift"
          confirmLabel="Clock Out"
          cancelLabel="Stay clocked in"
          confirmTone="danger"
          busy={this.state.clockOutProcessing}
          onConfirm={this.confirmClockOut}
          onCancel={this.cancelClockOut}
        />

        {this.displayTable()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  status: statusSelector(state),
  user: userInfoSelector(state),
  clockRecord: clockRecordSelector(state),
});

const actionCreators = {
  clearStatus,
  clockIn,
  clockOut,
  loadClockRecords,
};

export default withShipment(
  {
    actionCreators,
    mapStateToProps,
  },
  ClockInOut
);
